/**
 * Per-key throttle with a trailing call: emits at most once per `windowMs` for each key and
 * always delivers the latest value at the end of a window, so the final position is never lost.
 */
export function createKeyedThrottle<T>(windowMs: number, emit: (value: T) => void, now: () => number = Date.now) {
  const lastEmitAt = new Map<string, number>()
  const pending = new Map<string, T>()
  const timers = new Map<string, ReturnType<typeof setTimeout>>()

  const flushKey = (key: string) => {
    timers.delete(key)
    if (!pending.has(key)) return
    const value = pending.get(key) as T
    pending.delete(key)
    lastEmitAt.set(key, now())
    emit(value)
  }

  return {
    push(key: string, value: T) {
      const at = now()
      const last = lastEmitAt.get(key)
      if ((last == null || at - last >= windowMs) && !timers.has(key)) {
        lastEmitAt.set(key, at)
        emit(value)
        return
      }
      pending.set(key, value)
      if (!timers.has(key)) {
        const wait = last == null ? windowMs : Math.max(0, windowMs - (at - last))
        timers.set(key, setTimeout(() => flushKey(key), wait))
      }
    },
    cancel() {
      timers.forEach((timer) => clearTimeout(timer))
      timers.clear()
      pending.clear()
    },
  }
}
